<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef } from "vue";

import { cardActions } from "@/action/cardActions";
import { action } from "@/components/action/action";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { useShortcuts } from "@/composables/useShortcuts";
import { isFrontendBoard } from "@/model/board";
import type { StickyType } from "@/model/stickyType";
import { useBoardStore } from "@/store/board";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useUserStore } from "@/store/user";
import { contrastCssColor, toCssColor } from "@/utils/color";

import AddItemCard from "./AddItemCard.vue";

const choose = ref(false);
const readonly = computed(() => !useUserStore().isAllowed("edit"));

const { registerShortcut } = useShortcuts();
registerShortcut(cardActions.addAtPointer);

const types = computed(() => useBoardStore().creatableStickyTypes);
const currentType = computed(() =>
  types.value.find((t) => t.id === useClientSettingsStore().defaultStickyTypeId),
);
const hasCustomColor = computed(() => !!currentType.value?.color);
const colors = computed(() => {
  const current = currentType.value?.color;
  return current
    ? { backgroundColor: toCssColor(current), color: contrastCssColor(current) }
    : undefined;
});
const disabled = computed(() => isFrontendBoard(useBoardStore().currentBoard().type));

const closeButtonRef = useTemplateRef("closeButton");
const triggerButtonRef = useTemplateRef("triggerButton");

async function toggle() {
  if (!readonly.value) {
    choose.value = !choose.value;

    // focus on the close button when expanded
    // focus on the trigger button when collapsed
    await nextTick();
    if (choose.value) {
      closeButtonRef.value?.focus();
    } else {
      triggerButtonRef.value?.focus();
    }
  }
}

function select(type: StickyType) {
  useClientSettingsStore().setDefaultStickyType(currentType.value === type ? undefined : type);
  choose.value = false;
}

function addCard(type: StickyType) {
  void cardActions.add("board-actions-menu-click", { type, pos: "auto" }, true);
}
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
  <div class="add-item" :class="{ choose, disabled }" @click="toggle">
    <div class="items">
      <div class="popout" role="menu">
        <BaseTooltip v-for="type in types" :key="type.id" position="top" :delay="0">
          <AddItemCard
            :type
            :current="type === currentType"
            :selectable="choose"
            @select="select"
            @create="addCard"
          />
          <template #content>{{ type.name }}</template>
        </BaseTooltip>
        <IconButton
          ref="closeButton"
          :tabindex="choose ? 0 : -1"
          icon="system/close"
          :aria-label="$t('general.close')"
          aria-expanded="true"
        />
      </div>
    </div>
    <div v-if="!choose" class="toggle-button-wrapper">
      <IconButton
        ref="triggerButton"
        v-bind="action(cardActions.togglePalette)"
        aria-expanded="false"
        class="toggle-button"
        :activated="!hasCustomColor"
        :style="colors"
      />
    </div>
  </div>
</template>

<style lang="scss">
@use "@/styles/variables/colors";

.add-item {
  position: relative;
  border-radius: 8px;
  overflow: visible;
  height: 40px;
  min-width: 40px;
  padding: 2px;

  .toggle-button-wrapper {
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;

    .toggle-button {
      width: 40px !important;
      height: 40px !important;
      border-radius: 8px;
    }

    &:focus-within {
      outline: 2px solid colors.$outline;
    }
  }

  .items {
    display: flex;
    overflow: hidden;
    align-items: center;
  }

  .popout {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    max-width: 0;
    opacity: 0;
    transform: scale(0.95);
    transform-origin: center;
    transition:
      max-width 0.3s ease-in-out,
      opacity 0.3s ease-in-out,
      transform 0.3s ease-in-out;
  }

  &.choose {
    .popout {
      max-width: 1000px;
      opacity: 1;
      transform: scale(1);
    }
  }
}

/* Hide parent menu-group background when collapsed */
.menu-group-add-item:has(.add-item:not(.choose)) {
  background-color: transparent !important;
}
</style>
